// useModalAccessibility
// ----------------------
// Drop-in hook that gives a portaled modal three pieces of accessibility
// behavior the test plan calls for:
//
//   1. Escape closes the modal.
//   2. On open, focus moves into the modal (first focusable element, or
//      the modal container itself as a fallback).
//   3. On close, focus returns to whatever element opened the modal —
//      typically the trigger button — so keyboard users land back where
//      they were instead of at the top of the document.
//
// Usage:
//
//   const dialogRef = useRef<HTMLDivElement>(null);
//   useModalAccessibility(isOpen, onClose, dialogRef);
//   ...
//   ReactDOM.createPortal(
//     <div ref={dialogRef} role="dialog" aria-modal="true"> ... </div>,
//     document.body
//   );
//
// Why the trigger element is captured at open-time and not passed in:
// the consumer rarely has a stable ref to the trigger (it's often deep
// in a list of buttons). Reading document.activeElement at the moment
// the modal opens captures whichever element actually had focus, which
// is what users perceive as "the thing I clicked".
//
// Out of scope: focus *trap* (Tab cycling within the modal). Doing that
// safely needs walking the focusable-element tree on every Tab and
// dealing with shadow DOM, dynamic content, and modal-inside-modal
// scenarios. The plan calls it out as a follow-up; flagged in
// TestPlan_Results11May2026.md.

import { RefObject, useEffect, useRef } from "react";

// CSS selector for "things a keyboard user can focus". Mirrors the list
// commonly used by focus-trap libraries; intentionally excludes
// elements with `tabindex="-1"` (programmatic focus only) and disabled
// inputs (browser already skips them).
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export function useModalAccessibility(
  isOpen: boolean,
  onClose: () => void,
  modalContainerRef: RefObject<HTMLElement>
): void {
  // Stash the trigger element so we can restore focus to it on close.
  // Captured at the moment the modal opens — closing then reads from
  // this ref instead of document.activeElement (which by then is inside
  // the modal).
  const triggerElementRef = useRef<HTMLElement | null>(null);

  // Escape-to-close. Bound only while the modal is open so multiple
  // stacked modals don't all fire on one keystroke.
  useEffect(() => {
    if (!isOpen) return;

    const handleEscapeKey = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        keyboardEvent.stopPropagation();
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isOpen, onClose]);

  // Focus management. On open we capture the trigger and shift focus
  // into the modal; on close we restore focus to the trigger.
  useEffect(() => {
    if (!isOpen) return;

    // Capture the element that had focus when the modal opened. This
    // runs after the click handler that called setIsOpen(true), so
    // document.activeElement is still the clicked button.
    const previouslyFocusedElement =
      (document.activeElement as HTMLElement | null) ?? null;
    triggerElementRef.current = previouslyFocusedElement;

    // Defer the focus shift one frame so the portal contents are in the
    // DOM before we query for focusable children. Without this, the
    // querySelector below runs against an empty container and falls
    // through to focusing the container itself.
    const focusFirstElementInModal = () => {
      const modalNode = modalContainerRef.current;
      if (!modalNode) return;
      const firstFocusableElement =
        modalNode.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      } else {
        // Fallback — make the modal container itself focusable so screen
        // readers and Tab-key users have a sensible starting point.
        modalNode.setAttribute("tabindex", "-1");
        modalNode.focus();
      }
    };
    const animationFrameHandle = requestAnimationFrame(focusFirstElementInModal);

    return () => {
      cancelAnimationFrame(animationFrameHandle);
      // Restore focus on close. Guard against the trigger having been
      // unmounted (e.g. user deleted the row whose Manage button opened
      // the modal) — fall back to no-op rather than throwing.
      const triggerElement = triggerElementRef.current;
      triggerElementRef.current = null;
      if (triggerElement && document.body.contains(triggerElement)) {
        triggerElement.focus();
      }
    };
  }, [isOpen, modalContainerRef]);
}

export default useModalAccessibility;
